import React, {useState, useEffect, useRef} from 'react';
import {FaClock, FaCheckCircle} from 'react-icons/fa';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate} from 'react-router';
import {ContentHeader, Loader} from '@components';
import {logoutUser} from '@app/store/reducers/auth';
import {format, differenceInSeconds} from 'date-fns';
import {useFormik} from 'formik';
import {toast} from 'react-toastify';
import ShowDailyLogTime from '@app/components/show-daily-log';
import {
  saveDailyGoals,
  getTodayDaily,
  updateDailies,
  getTodayLogUpdates
} from '../../services/daily';
import Editor from '../../components/text-editor';
import handleStatus from '../../utils/ajax';
import {timeZone} from '../../utils/date';
import 'react-toastify/dist/ReactToastify.css';
// eslint-disable-next-line import/order
import {removeTags} from '@app/utils/helpers';

interface InitialValue {
  goals?: string;
  status?: string;
  breakTime?: string;
}

interface User {
  id: string;
  name: string;
  email: string;
  data: {
    permissions: string[];
  };
}

interface AuthState {
  currentUser: User | null;
}

interface RootState {
  auth: AuthState;
}

const Daily = () => {
  const dispatch = useDispatch();
  const [goals, setGoals] = useState('');
  const [status, setStatus] = useState('');
  const [submitRecap, setSubmitRecap] = useState(false);
  const [todayLogs, setTodayLogs] = useState([]);
  const [recapwork, setRecapwork] = useState(true);
  const [showLoader, setShowLoader] = useState(false);
  const [dailyId, setDailyId] = useState(null);
  const [disableSubmitButton, setDisableSubmitButton] = useState(false);
  const [initalValue, setInitialValue] = useState<InitialValue>({});
  const [breakTime, setBreakTime] = useState<string>('');
  const [saveBreakTime, setSaveBreakTime] = useState<string>('');
  const [startTime, setStartTime] = useState<Date | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState<number>(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const checkboxRef = useRef();
  const user = useSelector((state: RootState) => state.auth.currentUser);
  const navigate = useNavigate();

  const getTodayDailyRecord = async () => {
    setShowLoader(true);

    const resp = await getTodayDaily();
    handleStatus(resp.status, () => dispatch(logoutUser()), navigate);
    if (resp.data.ok === true) {
      setShowLoader(false);

      const dailiesData = resp.data.data;
      if (!dailiesData) {
        return;
      }

      const {
        _id: dailyId,
        created_At: createdAt,
        updated_At: updatedAt,
        status: dailyStatus
      } = dailiesData;
      console.log(dailiesData, 'aaaaaaaaaaaaaaaaaaaa');
      setDailyId(dailyId);

      setInitialValue(dailiesData);
      setBreakTime(dailiesData?.breakTime);
      setSaveBreakTime(dailiesData?.breakTime);

      if (dailyStatus) {
        setRecapwork(true);
        setDisableSubmitButton(true);
        stopTimer();
      } else if (createdAt && !dailyStatus) {
        // Goals submitted but status not submitted - timer should be running
        setRecapwork(false);
        setDisableSubmitButton(false);
        startTimerFromSavedTime(createdAt);
      } else {
        // No goals submitted yet - timer should not be running
        setRecapwork(true);
        setDisableSubmitButton(false);
        stopTimer();
      }
    } else {
      setShowLoader(false);
      // notication something went wrong
    }
  };

  useEffect(() => {
    getTodayDailyRecord();
    return () => {
      stopTimer();
    };
  }, []);

  const startTimerFromSavedTime = (createdAt: string) => {
    const now = new Date();
    const createdTime = new Date(createdAt);
    const secondsElapsed = differenceInSeconds(now, createdTime);
    setElapsedSeconds(secondsElapsed);
    startTimer();
  };

  const startTimer = () => {
    if (!timerRef.current) {
      timerRef.current = setInterval(() => {
        setElapsedSeconds((prev) => prev + 1);
      }, 1000);
    }
  };

  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const formatTimer = (totalSeconds: number) => {
    const hrs = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;
    return `${hrs.toString().padStart(2, '0')}:${mins
      .toString()
      .padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    if (startTime) {
      timerRef.current = setInterval(() => {
        setElapsedSeconds((prev) => prev + 1);
      }, 1000);
    } else if (!startTime && timerRef.current) {
      clearInterval(timerRef.current);
    }

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [startTime]);

  const initialValues = {
    goals: '',
    status: '',
    breakTime: ''
  };

  const parseHoursAndMinutes = (time: string): number[] =>
    time
      .split(/[hHmM]/)
      .filter(Boolean)
      .map(Number);

  const calculateTotalLoggedHours = (loggedHours: []) => {
    let totalLoggedHours = 0;
    loggedHours.forEach((log) => {
      const {timeSpent = []} = log;

      timeSpent.forEach(({time}) => {
        const [hours = 0, minutes = 0] = parseHoursAndMinutes(time);
        const totalMinutes = hours * 60 + minutes;
        totalLoggedHours += totalMinutes;
        // console.log(totalMinutes, "hours********************")
      });
    });

    // console.log(totalLoggedHours, "totalLoggedHours********************");

    return totalLoggedHours;
  };

  const formatEstimateTime = (minutes: number) => {
    if (Number.isNaN(minutes) || minutes < 0) {
      // console.log("*****formatEstimateTime 1******************", minutes)
      return 'N/A';
    }

    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    if (hours === 0 && remainingMinutes > 0) {
      return `${remainingMinutes}m`;
      // eslint-disable-next-line no-else-return
    } else if (hours > 0 && remainingMinutes === 0) {
      return `${hours}h`;
    } else if (hours > 0 && remainingMinutes > 0) {
      return `${hours}h${remainingMinutes}m`;
    }

    return '0m';
  };
  const countWords = (text: string) => {
    return text.trim().split(/\s+/).length;
  };

  const onSubmit = async (values: any) => {
    if (goals && countWords(goals) < 20) {
      toast.error('Goals length should not be less than 20 words');
      return;
    }

    if (status && countWords(status) < 20) {
      toast.error('Status length should not be less than 20 words');
      return;
    }

    if (countWords(goals) > 1000) {
      toast.error('Goals length should not be greater than 1000 words');
      return;
    }

    if (countWords(status) > 1000) {
      toast.error('Status length should not be greater than 1000 words');
      return;
    }

    if (!initalValue.goals && !removeTags(goals)) {
      toast.error('Please enter your goals');
      return;
    }

    if (
      !initalValue.status &&
      !breakTime &&
      initalValue.goals &&
      !removeTags(status)
    ) {
      toast.error('Please enter your daily');
      return;
    }

    if (!recapwork && !removeTags(status) && !breakTime) {
      toast.error('Please enter your break time or status');
      return;
    }

    if (!recapwork && saveBreakTime && !removeTags(status)) {
      toast.error('Please enter your status');
      return;
    }

    if (recapwork === true) {
      setShowLoader(true);
      const result = await saveDailyGoals({
        goals,
        timeZone: timeZone()
      });

      if (!result?.data?.ok) {
        setShowLoader(false);
        toast.error(
          result?.data?.error ||
            result?.data?.message ||
            'Something went wrong while creating dailies.'
        );
        return;
      }

      const responseData = result?.data?.data;
      if (responseData) {
        const {_id: id} = responseData;
        localStorage.setItem('goalsId', id);
        setDailyId(id);
      }

      setShowLoader(false);
      setRecapwork(false);

      setDisableSubmitButton(false);
      toast.success('Your goal saved successfully');
      // Start timer when goals are first submitted
      startTimer();
      getTodayDailyRecord();
    }

    if (recapwork === false) {
      if (submitRecap) {
        if (user?.data?.permissions.includes('hr')) {
          setShowLoader(true);
          const updateDailiesApiResponse = await updateDailies(
            {status, timeZone: timeZone(), breakTime},
            dailyId
          );
          if (!updateDailiesApiResponse?.data?.ok) {
            setShowLoader(false);
            toast.error(
              updateDailiesApiResponse?.data?.error ||
                updateDailiesApiResponse?.data?.message ||
                'Something went wrong while submitting recap.'
            );
            return;
          }
          setRecapwork(true);
          setShowLoader(false);

          setDisableSubmitButton(true);
          toast.success('Your recap saved successfully');
          // Stop timer when status is submitted
          stopTimer();
          getTodayDailyRecord();
          setSubmitRecap(false);
          return;
        }

        // const logStatus = handleCheckboxChange();

        // if (!logStatus.ok) {
        //   return toast.error(logStatus.error)
        // }

        // const totalHours = calculateTotalLoggedHours(todayLogs);
        // const totalMinsInEightHours = 8 * 60;

        // if (totalHours < totalMinsInEightHours) {
        //   return toast.error(`Your total hours are less than 8. Total logged hours for today are ${formatEstimateTime(totalHours)}`)
        // }
        // console.log(status, 'status**************************');
        setShowLoader(true);
        const updateDailiesApiResponse = await updateDailies(
          {status, timeZone: timeZone(), breakTime},
          dailyId
        );
        if (!updateDailiesApiResponse?.data?.ok) {
          setShowLoader(false);

          toast.error(
            updateDailiesApiResponse?.data?.error ||
              updateDailiesApiResponse?.data?.message ||
              'Something went wrong while submitting recap.'
          );

          return;
        }
        setRecapwork(true);
        setShowLoader(false);

        setDisableSubmitButton(true);
        toast.success('Your recap saved successfully');
        getTodayDailyRecord();
        // Stop timer when status is submitted
        stopTimer();
        setSubmitRecap(false);
      } else {
        const resp = await getTodayLogUpdates();
        if (resp.status === 200) {
          const {data} = resp;
          setTodayLogs(data.data);
          setSubmitRecap(true);
        }
      }
    }
  };

  // const validate = (values: any) => {
  //   return {};
  // };

  const formik = useFormik({
    initialValues,
    onSubmit
    // validate
  });

  // const formatTimeInHoursAndMinutes = (totalMinutes) => {
  //   if (Number.isNaN(totalMinutes) || totalMinutes < 0) {
  //     return 'N/A';
  //   }

  //   const hours = Math.floor(totalMinutes / 60);
  //   const minutes = totalMinutes % 60;

  //   if (hours > 0 && minutes > 0) {
  //     return `${hours}h${minutes}m`;
  //   } else if (hours > 0) {
  //     return `${hours}h`;
  //   } else if (minutes > 0) {
  //     return `${minutes}m`;
  //   }

  //   return 'N/A';
  // };

  // const calculateTime = (time) => {
  //   let totalTime = 0;
  //   time.forEach((task) => {
  //     const [hours, minutes] = parseHoursAndMinutes(task.time);

  //     totalTime += hours * 60 + minutes;
  //   });

  //   return formatTimeInHoursAndMinutes(totalTime);
  // };

  // const handleCheckboxChange = () => {
  //   try {
  //     const isChecked = checkboxRef.current.checked;
  //     return {
  //       ok: isChecked,
  //       error: isChecked ? null : 'Please acknowledge the logged hours'
  //     };
  //   } catch (error) {
  //     return {ok: false, error: `Please log today's hours`};
  //   }
  // };

  if (showLoader) {
    return <Loader title="Daily" />;
  }

  return (
    // <React.Fragment>
    <>
      <ContentHeader title="Daily" />

      <section className="content daily-wrapper">
        <div className="row">
          <div className="col-md-12">
            <div className="card card-outline card-info">
              <div className="card-header">
                <h3 className="card-title">
                  Please enter your daily for{' '}
                  {format(new Date(), ' do MMM, yyyy')}
                </h3>
              </div>
              <div className=" card-body">
                <div className="d-flex gap-3">
                  <div className="col-md-8">
                    <div className="card card-primary">
                      <form onSubmit={formik.handleSubmit}>
                        <div className="card-body">
                          <div className="form-group">
                            <label htmlFor="exampleInputEmail1">
                              Please enter your goals of the day
                            </label>
                            {initalValue.goals ? (
                              <div
                                className="filled-values"
                                dangerouslySetInnerHTML={{
                                  __html: initalValue.goals
                                }}
                              />
                            ) : (
                              <Editor
                                value=""
                                onChange={(e: any) => setGoals(e)}
                                height={0}
                              />
                            )}
                          </div>

                          <div className="form-group">
                            <label htmlFor="exampleInputEmail1">
                              Please enter your recap of the day
                            </label>
                            {initalValue.status || recapwork ? (
                              <div
                                className="filled-values"
                                dangerouslySetInnerHTML={{
                                  __html: initalValue.status ?? ''
                                }}
                              />
                            ) : (
                              <Editor
                                value=""
                                onChange={(e: any) => setStatus(e)}
                                height={0}
                              />
                            )}

                            {!recapwork && todayLogs.length > 0 ? (
                              <ShowDailyLogTime isAdmin={false} />
                            ) : null}
                          </div>
                          {/* {!recapwork && todayLogs.length > 0 ? (
                            <div className="form-group">
                              <div className="form-check">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id="acknowledgeCheckbox"
                                  ref={checkboxRef}
                                  onChange={handleCheckboxChange}
                                />
                                <label
                                  className="form-check-label"
                                  for="acknowledgeCheckbox"
                                >
                                  I acknowledge the logged hours
                                </label>
                              </div>
                            </div>) : null} */}

                          {initalValue.goals && (
                            <div className="col-lg my-4">
                              {/* Display Selected Time */}
                              {!disableSubmitButton && !saveBreakTime && (
                                <>
                                  {' '}
                                  <label
                                    htmlFor="breakTime"
                                    className="block text-lg font-semibold text-gray-700 dark:text-gray-200 mb-2"
                                  >
                                    <FaClock className="me-2 text-primary" />{' '}
                                    &nbsp; Please enter your break time of the
                                    day
                                  </label>
                                  {/* Break Time Input Group */}
                                  <div className="flex items-center space-x-3">
                                    {/* Hour Selector */}
                                    <select
                                      className="w-1/2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white text-sm rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 p-2 transition duration-300"
                                      value={
                                        breakTime
                                          ? breakTime.match(/(\d+)h/)?.[1]
                                          : '0'
                                      } // Default to 0h
                                      onChange={(e) =>
                                        setBreakTime(
                                          `${e.target.value}h${
                                            breakTime
                                              ? breakTime.match(/(\d+)m/)?.[0]
                                              : ''
                                          }`
                                        )
                                      }
                                    >
                                      <option value="0">0 Hours</option>
                                      {Array.from({length: 5}, (_, i) => {
                                        const hourValue = i + 1; // Assign a unique value
                                        return (
                                          <option
                                            key={`hour-${hourValue}`}
                                            value={hourValue}
                                          >
                                            {hourValue} Hours
                                          </option>
                                        );
                                      })}
                                    </select>

                                    {/* Minute Selector */}
                                    <select
                                      className="w-1/2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white text-sm rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 p-2 transition duration-300"
                                      value={
                                        breakTime
                                          ? breakTime.match(/(\d+)m/)?.[1]
                                          : '0'
                                      }
                                      onChange={(e) =>
                                        setBreakTime(
                                          `${
                                            breakTime
                                              ? breakTime.match(/(\d+)h/)?.[0]
                                              : '0h'
                                          }${e.target.value}m`
                                        )
                                      }
                                    >
                                      <option value="0">0 Minutes</option>
                                      {Array.from(
                                        {length: 55},
                                        (_, i) => i + 1
                                      ).map((m) => (
                                        <option key={m} value={m}>
                                          {m} Minutes
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                </>
                              )}
                              {breakTime && (
                                <p className="mt-3 text-lg text-gray-700 dark:text-gray-300 font-medium">
                                  Selected Break Time:{' '}
                                  <span className="text-blue-600 dark:text-blue-400 font-bold">
                                    {breakTime}
                                  </span>
                                </p>
                              )}
                            </div>
                          )}

                          <div className="form-group">
                            <button
                              type="submit"
                              disabled={disableSubmitButton}
                              className="btn btn-primary"
                            >
                              {submitRecap ? 'Submit Recap' : 'Submit'}
                            </button>
                          </div>
                        </div>
                      </form>
                    </div>
                  </div>

                  {/* Show timer only when goals are submitted but status is not submitted yet */}
                  {initalValue.goals && !initalValue.status && (
                    <div className="p-4 bg-green-50 dark:bg-green-900 rounded-lg shadow-sm">
                      <div className="flex items-center gap-3">
                        {/* Watch Icon */}
                        <div className="text-center bg-blue-100 dark:bg-blue-900 p-4 rounded-full">
                          <FaClock className="text-blue-600 dark:text-blue-300 text-3xl" />
                        </div>

                        {/* Timer Text */}
                        <div>
                          <p className="text-sm font-medium text-gray-600 dark:text-gray-300">
                            Time spent on work today
                          </p>
                          <div
                            style={{fontSize: '42px'}}
                            className="font-bold text-blue-600 dark:text-blue-400 leading-snug"
                          >
                            {formatTimer(elapsedSeconds)}
                          </div>
                          <p className="text-gray-400 dark:text-gray-500">
                            HH:MM:SS
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {initalValue.goals && initalValue.status && (
                    <div className="p-4 bg-green-50 dark:bg-green-900 rounded-lg shadow-sm">
                      <div className="flex items-center gap-3">
                        <div className="text-center bg-green-100 dark:bg-green-800 p-2 rounded-full">
                          <FaCheckCircle className="text-green-600 dark:text-green-300 text-xl" />
                        </div>

                        <div>
                          <p className="text-sm font-semibold text-green-700 dark:text-green-200">
                            Total hours of the day
                          </p>
                          {initalValue?.totalHours && (
                            <p className="text-green-800 dark:text-green-300 mt-1">
                              Total Hours:{' '}
                              <span style={{fontSize: '24px'}}>
                                {initalValue.totalHours}
                              </span>
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="card-footer">
                Note:- Please make sure you enter your appropriate Goals and
                Daily
              </div>
            </div>
          </div>
        </div>
      </section>
    </>

    // </React.Fragment>
  );
};

export default Daily;
